/**
 * EasyRohbau Logo
 * - Architectural mark: 4 stacked horizontal bars (floor slabs) forming an "E",
 *   subtle blueprint precision with a diagonal accent cut (the construction angle).
 * - Wordmark: "Easy" in accent blue, "Rohbau" in deep navy → premium split.
 */
export function LogoMark({ size = 36, className = "" }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="er-grad" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stopColor="#0C2749" />
          <stop offset="100%" stopColor="#2873B8" />
        </linearGradient>
        <linearGradient id="er-stone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFFFFF" />
          <stop offset="100%" stopColor="#E6EEF7" />
        </linearGradient>
      </defs>

      {/* Background block */}
      <rect x="0" y="0" width="40" height="40" rx="3" fill="url(#er-grad)" />

      {/* Three stacked stones forming an "E" (full / short / full),
          left edges aligned to suggest the vertical spine. */}

      {/* Top stone (full width) */}
      <rect x="7"  y="8.5"  width="26" height="6.5" rx="0.8" fill="url(#er-stone)" />
      {/* mortar joint cuts (vertical) – give brick texture */}
      <rect x="16" y="9.2"  width="0.6" height="5.1" fill="#0C2749" opacity="0.18" />
      <rect x="24" y="9.2"  width="0.6" height="5.1" fill="#0C2749" opacity="0.18" />
      {/* subtle bottom shadow */}
      <rect x="7"  y="14.6" width="26" height="0.6" fill="#0C2749" opacity="0.12" />

      {/* Middle stone (shorter – the middle bar of E) */}
      <rect x="7"  y="17"   width="18" height="6"   rx="0.8" fill="url(#er-stone)" />
      <rect x="14" y="17.6" width="0.6" height="4.8" fill="#0C2749" opacity="0.18" />
      <rect x="7"  y="22.6" width="18" height="0.6" fill="#0C2749" opacity="0.12" />

      {/* Bottom stone (full width) */}
      <rect x="7"  y="25"   width="26" height="6.5" rx="0.8" fill="url(#er-stone)" />
      <rect x="17" y="25.7" width="0.6" height="5.1" fill="#0C2749" opacity="0.18" />
      <rect x="25" y="25.7" width="0.6" height="5.1" fill="#0C2749" opacity="0.18" />

      {/* Construction accent dot (lower-right) */}
      <circle cx="34" cy="33.5" r="1.8" fill="#2C80C8" />
    </svg>
  );
}

export function LogoWordmark({ className = "", showMark = true, markSize = 36, withSlogan = false }) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      {showMark && <LogoMark size={markSize} />}
      <span className="flex flex-col">
        <span
          className="font-semibold tracking-tight text-lg leading-none"
          style={{ fontFamily: "Outfit" }}
        >
          <span className="text-[#2873B8]">Easy</span>
          <span className="text-[#0C2749]">Rohbau</span>
        </span>
        {withSlogan && (
          <span
            className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#2873B8]/80 mt-1 leading-none"
            style={{ fontFamily: "Manrope" }}
          >
            Stein für Stein zu Ihrem Heim.
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * Inverse variant for dark backgrounds (Footer, Admin header).
 */
export function LogoWordmarkInverse({ className = "", markSize = 36, withSlogan = false }) {
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`}>
      <LogoMark size={markSize} />
      <span className="flex flex-col">
        <span
          className="font-semibold tracking-tight text-lg leading-none"
          style={{ fontFamily: "Outfit" }}
        >
          <span className="text-[#2C80C8]">Easy</span>
          <span className="text-white">Rohbau</span>
        </span>
        {withSlogan && (
          <span
            className="text-[10px] font-medium tracking-[0.18em] uppercase text-[#2C80C8] mt-1 leading-none"
            style={{ fontFamily: "Manrope" }}
          >
            Stein für Stein zu Ihrem Heim.
          </span>
        )}
      </span>
    </span>
  );
}

export default LogoWordmark;
